import React from "react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { Links, Meta, Scripts, useRouteError } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE } from "@/app/utils/i18n";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error }>;
}

interface ErrorFallbackProps {
  error: Error;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error }) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="text-center">
          <h1 className="text-lg font-medium text-gray-900 mb-2">
            {t("Something went wrong")}
          </h1>
          <p className="text-sm text-gray-500 mb-4">
            {t("We've been notified and are working to fix the issue.")}
          </p>
          {process.env.NODE_ENV === "development" && (
            <details className="text-left mb-4">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                {t("Error Details")}
              </summary>
              <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t("Try again")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback: FallbackComponent = DefaultErrorFallback,
}) => {
  const error = useRouteError();

  if (error) {
    captureRemixErrorBoundaryError(error);

    return <FallbackComponent error={error as Error} />;
  }

  return <>{children}</>;
};

/**
 * Root-level boundary. It replaces the whole App, so it has to render the
 * document itself: without <Links /> the error page would arrive with no
 * stylesheet at all and fall back to browser defaults.
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const { i18n } = useTranslation();
  captureRemixErrorBoundaryError(error);

  return (
    <html lang={i18n.language || DEFAULT_LANGUAGE} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning>
        <DefaultErrorFallback error={error as Error} />
        <Scripts />
      </body>
    </html>
  );
};
